/**
 * @typedef {typeof import('widgets/global/Tabs').default} Tabs
 */

import { scrollWindowTo, scrollToTop } from 'widgets/toolbox/scroll';

/**
 * @description Base Tabs implementation
 * @param Tabs
 * @param {Tabs} Widget - widget for extending
 * @returns {typeof HarmonyTabs} Tabs widget
 */
export default function (Tabs) {
    /**
     * @class HarmonyTabs
     * @augments Tabs
     */
    class HarmonyTabs extends Tabs {
        init() {
            super.init();

            /**
             * When verification code form need to be shown.
             */
            this.eventBus().on('verificationcodeform.show', 'onVerificationShow');

            const urlParams = new URLSearchParams(window.location.search);
            const showRegisterTab = urlParams.has('register');

            // Activate the register panel if the 'register' parameter is present
            if (showRegisterTab) {
                this.activatePanel('register');
            }
        }

        /**
         * @param {InstanceType<Widget>} clickedButton - Widget, representing customer's tab clicked element
         * @returns {void}
         */
        handleTabClick(clickedButton) {
            const panelName = String(clickedButton.config.panelName);

            this.activatePanel(panelName);
            this.afterTabChanged(panelName);
        }

        /**
         * @description Sets focus to panel with given name
         * <br>Uses `roving focus` accessibility feature
         * <br>https://www.w3.org/TR/wai-aria-practices/#kbd_roving_tabindex
         * @param {string} tab Tab name to set focus to
         * @returns {void}
         */
        setFocusToTab(tab) {
            if (!tab) {
                return;
            }
            super.setFocusToTab(tab);
            this.afterTabChanged(tab);
        }

        /**
         * @description when verification code form has been shown.
         * @param {string} form - which specific form.
         */
        onVerificationShow(form) {
            switch (form) {
                case 'login':
                    this.ref('login').attr('tabindex', '-1');
                    break;
                case 'register':
                    this.ref('register').attr('tabindex', '-1');
                    setTimeout(() => {
                        this.ref('register') ? scrollWindowTo(this.ref('register').get(), true) : scrollToTop();
                    }, 200);
                    break;
                default:
                    break;
            }
        }

        /**
         * @description when tab was changes
         * @param {string} tabName - the name of triggered tab.
         */
        afterTabChanged(tabName) {
            this.ref('login').get().removeAttribute('tabindex');
            this.ref('register').get().removeAttribute('tabindex');

            this.eventBus().emit('harmonytab.change', tabName);
        }
    }

    return HarmonyTabs;
}
